import {
  AgGrid,
  MobileResponsiveGrid,
  Button,
  ErrorMessage,
  Heading,
  Spinner,
  InfoPopup,
} from "@/common/components";
import {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useSelector,
} from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetInsuranceCompanyContactQuery,
  useDeleteInsuranceCompanyContactForAdminMutation,
  useGetInsuranceCompaniesForAdminQuery,
} from "@/service/AdminService";
import { setInsuranceCompaniesData } from "@/store/adminSlice";
import { formatPhoneNumber, formatRoleCode } from "@/utils/Utilities";

const InsuranceCompanies = () => {
  const {
    setNavHeader,
    skipToken,
    Routes,
    navigate,
    labels,
    setPageHeader,
    dispatch,
    setNavCancelFunc,
    actionsDropdown,
  } = useContext(PrivateContext);

  const [rowDataForInsurance, setRowDataForInsurance] = useState([]);
  const [insuranceId, setInsuranceId] = useState("");
  const [contactsParam, setContactsParam] = useState(skipToken);
  const [rowDataContacts, setRowDataContacts] = useState([]);
  const [showConfirmPopup, setShowConfirmPopup] = useState("");
  const [sortOptionCompany, setSortOptionCompany] = useState("Name (A to Z)");
  const [sortOptionContact, setSortOptionContact] = useState("Role (A to Z)");

  const storeData = useSelector(
    (state) => state?.adminReducer?.insuranceCompanies
  );

  const columnDefsForInsurance = [
    {
      headerName: labels?.insuranceCompany?.companyNameHeader,
      field: "companyName",
      flex: 1.5,
      sortable: true,
    },
    {
      headerName: labels?.insuranceCompany?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.insuranceCompany?.viewDetails,
            path: Routes.insuranceCompanyDetails.path,
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.insuranceCompany?.insurerIdHeader,
      field: "id",
    },
    {
      headerName: labels?.insuranceCompany?.serverHostNameHeader,
      field: "serverHostName",
    },
    {
      headerName: labels?.insuranceCompany?.hostPortNumberHeader,
      field: "hostPortNumber",
    },
  ];

  const columnDefsContacts = [
    {
      headerName: labels?.insuranceCompany?.role,
      field: "roleCode",
      cellRenderer: formatRoleCode,
      sortable: true,
    },
    {
      headerName: labels?.insuranceCompany?.actions,
      colId: "actions",
      cellRenderer: actionsDropdown,
      cellRendererParams: {
        list: [
          {
            text: labels?.insuranceCompany?.viewDetails,
            click: (e) => getDetailsForContact(e),
          },
          {
            text: labels?.insuranceCompany?.removeContact,
            click: (e) => setShowConfirmPopup(e.id),
          },
        ],
      },
      sortable: false,
    },
    {
      headerName: labels?.insuranceCompany?.contactFirstName,
      field: "firstName",
    },
    {
      headerName: labels?.insuranceCompany?.contactLastName,
      field: "lastName",
    },
    {
      headerName: labels?.insuranceCompany?.contactPhone,
      field: "phoneNumber",
      cellRenderer: formatPhoneNumber,
    },
    {
      headerName: labels?.insuranceCompany?.contactEmail,
      field: "emailAddress",
      flex: 1.5,
    },
  ];

  const {
    data: insuranceCompanies,
    isFetching,
    error,
  } = useGetInsuranceCompaniesForAdminQuery();

  const {
    data: contacts,
    isFetching: isFetching1,
    error: error1,
  } = useGetInsuranceCompanyContactQuery(contactsParam);

  const [deleteContactApi, { isLoading: isFetching2, error: error2 }] =
    useDeleteInsuranceCompanyContactForAdminMutation();

  const clearStateInStore = () => {
    dispatch(setInsuranceCompaniesData({}));
    navigate(-1);
    return;
  };

  const onCellClicked = useCallback((e) => {
    if (e.colDef.colId !== "actions") {
      setInsuranceId(e.data.id);
    }
  }, []);

  const onRowClickedForAll = useCallback((e) => {
    // Use the data coming from the mobile grid - set the selected insurance id
    setInsuranceId(e.data?.id);
  }, []);

  const getRowClassInsurance = useCallback(
    (params) => {
      if (params.data.id === insuranceId) {
        return "hover-bg";
      }
      return "";
    },
    [insuranceId]
  );

  const onFirstDataRendered = useCallback(
    (params) => {
      const findIdx = rowDataForInsurance.findIndex(
        (e) => e.id === insuranceId
      );
      if (findIdx >= 0) {
        try {
          params.api.ensureIndexVisible(findIdx, "top");
        } catch (err) {
          // silently ignore if api not available
        }
      }
    },
    [rowDataForInsurance, insuranceId]
  );

  const addNewInsurance = () => {
    navigate(Routes.insuranceCompanyDetails.path);
  };

  const getDetailsForContact = useCallback(
    (params) => {
      const data = rowDataContacts.find((e) => e.id === params?.id);
      navigate(Routes.insuranceCompanyContactDetails.path, {
        state: {
          ...data,
          primaryExists:
            rowDataContacts.find((e) => e.roleCode === "P") &&
            data.roleCode !== "P",
        },
      });
    },
    [rowDataContacts]
  );

  const addNewContact = useCallback(() => {
    navigate(Routes.insuranceCompanyContactDetails.path, {
      state: {
        insuranceCompanyId: insuranceId,
        primaryExists: rowDataContacts.find((e) => e.roleCode === "P"),
      },
    });
  }, [navigate, Routes, insuranceId, rowDataContacts]);

  const removeContact = useCallback(() => {
    if (!showConfirmPopup) return;
    deleteContactApi({ contactId: showConfirmPopup, insuranceId })
      .unwrap()
      .then((res) => {
        if (!res.errors.length) {
          // Success - refetch happens via RTK Query
          setShowConfirmPopup("");
        }
      })
      .catch(() => {
        // handle error if needed
      });
  }, [showConfirmPopup, insuranceId]);

  const sortedEntitiesCompany = useMemo(() => {
    const arr = [...rowDataForInsurance];
    arr.sort((a, b) => {
      if (sortOptionCompany === "Name (A to Z)") {
        return a?.companyName?.localeCompare(b?.companyName);
      } else if (sortOptionCompany === "Name (Z to A)") {
        return b?.companyName?.localeCompare(a?.companyName);
      }
      return 0;
    });
    return arr;
  }, [rowDataForInsurance, sortOptionCompany]);

  const sortedEntitiesContacts = useMemo(() => {
    const arr = [...rowDataContacts];
    arr.sort((a, b) => {
      if (sortOptionContact === "Role (A to Z)") {
        return a?.roleCode?.localeCompare(b?.roleCode);
      } else if (sortOptionContact === "Role (Z to A)") {
        return b?.roleCode?.localeCompare(a?.roleCode);
      }
      return 0;
    });
    return arr;
  }, [rowDataContacts, sortOptionContact]);

  useEffect(() => {
    if (insuranceCompanies && insuranceCompanies?.insuranceCompanies) {
      setRowDataForInsurance(insuranceCompanies?.insuranceCompanies);
      if (!storeData?.insuranceId) {
        setInsuranceId(insuranceCompanies?.insuranceCompanies[0].id);
      }
    }
  }, [insuranceCompanies, storeData?.insuranceId]);

  useEffect(() => {
    if (contacts && contacts?.contacts) {
      setRowDataContacts(contacts?.contacts);
    }
  }, [contacts]);

  useEffect(() => {
    if (insuranceId) {
      dispatch(setInsuranceCompaniesData({ insuranceId: insuranceId }));
      setContactsParam({ insuranceCompanyId: insuranceId });
    }
  }, [insuranceId]);

  useEffect(() => {
    setPageHeader(labels?.insuranceCompany?.title);
    setNavHeader(labels?.navbar?.admin);
    setNavCancelFunc(() => clearStateInStore);

    if (storeData?.insuranceId) {
      setInsuranceId(storeData?.insuranceId);
    }
  }, []);

  return (
    <section className="container-large py-5">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage error={error || error1 || error2} wrapperClassName="mb-2" />

      <Button
        id="addCompany"
        text={labels?.insuranceCompany?.addInsuranceCompany}
        wrapperClassName="col-lg-3 col-md-4 mb-2"
        click={addNewInsurance}
      />

      <AgGrid
        wrapperClassNames="mb-5 d-none d-md-block"
        printClassNames="mt-md-n3"
        columnDefs={columnDefsForInsurance}
        rowData={rowDataForInsurance}
        domLayout={"normal"}
        onCellClicked={onCellClicked}
        getRowClass={getRowClassInsurance}
        onFirstDataRendered={onFirstDataRendered}
      />
      <MobileResponsiveGrid
        columnDefs={columnDefsForInsurance}
        selectedRowId={insuranceId}
        onRowClick={onRowClickedForAll}
        getRowClass={getRowClassInsurance}
        rowData={sortedEntitiesCompany}
        showSortDropDown={true}
        sortOption={sortOptionCompany}
        setSortOption={setSortOptionCompany}
      />
      <Heading
        text={labels?.insuranceCompany?.contacts}
        wrapperClassNames="h4 border-bottom-1 pb-2 my-3"
        title={false}
      />

      <Button
        id="addContact"
        text={labels?.insuranceCompany?.addContact}
        wrapperClassName="col-md-3 col-lg-2 mb-2"
        click={addNewContact}
      />

      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        printClassNames="mt-md-n3"
        columnDefs={columnDefsContacts}
        rowData={rowDataContacts}
        domLayout={"normal"}
        overlayNoRowsTemplate={labels?.insuranceCompany?.noContacts}
      />
      <MobileResponsiveGrid
        noDataLabel={labels?.insuranceCompany?.noContacts}
        rowData={sortedEntitiesContacts}
        columnDefs={columnDefsContacts}
        showSortDropDown={true}
        sortOption={sortOptionContact}
        setSortOption={setSortOptionContact}
      />

      <InfoPopup
        showModal={showConfirmPopup}
        primaryButtonText={labels?.insuranceCompany.ok}
        primaryButtonClick={removeContact}
        secondaryButtonText={labels?.insuranceCompany.cancel}
        secondaryButtonClick={() => setShowConfirmPopup("")}
        body={labels?.insuranceCompany.contactRemove}
      />
    </section>
  );
};

export default InsuranceCompanies;
